/**
 * The Briefed Book - projects at BRIEFED, mirroring the SOW Book
 * plus the extra timing leg SOW → Briefed (full chain Quote →
 * Commission → SOW → Briefed). Team/client drill-down cross-filters
 * everything; filters live in the URL (?team=…&client=…&book=briefed).
 */
import { useEffect, useMemo, useState, Fragment } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Loader2, AlertTriangle } from "lucide-react";
import {
  TeamPie, QualQuantMix, TopClients, MonthlyTrend,
  fmtGbp, fmtGbpShort, fmtDate, teamColor,
} from "./CommissionedPanels";
import { clientKey, buildClientIndex } from "./clientGrouping";
import {
  stat, groupAgg, fmtDelta, SortBtn, FilterChip, TimingChain, ValueVariance,
  InvoiceSchedule, InvoiceToggleCell,
} from "./bookShared";

const API = process.env.REACT_APP_BACKEND_URL;

export default function BriefedBook() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sortKey, setSortKey] = useState("brief_date");
  const [sortDir, setSortDir] = useState("desc");
  const [expandedCid, setExpandedCid] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const teamFilter = searchParams.get("team");
  const clientFilter = searchParams.get("client");

  useEffect(() => {
    axios.get(`${API}/api/proposals/pipeline/briefed-book`)
      .then((r) => { setData(r.data); setError(""); })
      .catch((e) => setError(e.response?.data?.detail || "Could not load the briefed book."))
      .finally(() => setLoading(false));
  }, []);

  const setFilter = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value == null) next.delete(key);
    else next.set(key, value);
    if (next.get("team") || next.get("client")) next.set("book", "briefed");
    else next.delete("book");
    setSearchParams(next, { replace: true });
  };
  const toggleTeam = (t) => setFilter("team", teamFilter === t ? null : t);
  const toggleClient = (c) => setFilter("client", clientFilter === c ? null : c);
  const clearAll = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("team");
    next.delete("client");
    next.delete("book");
    setSearchParams(next, { replace: true });
  };

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    else { setSortKey(key); setSortDir("desc"); }
  };

  const clientIndex = useMemo(() => buildClientIndex(data?.items || []), [data]);

  const view = useMemo(() => {
    const all = data?.items || [];
    const clientScoped = clientFilter ? all.filter((i) => clientKey(i) === clientFilter) : all;
    const teamScoped = teamFilter ? all.filter((i) => i.team === teamFilter) : all;
    const filtered = teamFilter ? clientScoped.filter((i) => i.team === teamFilter) : clientScoped;

    const byTeam = groupAgg(clientScoped, (i) => i.team)
      .map((g) => ({ team: g.key, count: g.count, value: g.value }))
      .sort((a, b) => b.count - a.count);

    let topClients = groupAgg(teamScoped, clientKey)
      .map((g) => ({
        key: g.key,
        company: clientIndex.get(g.key)?.label || g.key,
        variants: clientIndex.get(g.key)?.variants || [],
        count: g.count,
        value: g.value,
      }))
      .sort((a, b) => b.value - a.value);
    const head = topClients.slice(0, 8);
    if (clientFilter && !head.some((c) => c.key === clientFilter)) {
      const sel = topClients.find((c) => c.key === clientFilter);
      if (sel) head.push(sel);
    }
    topClients = head;

    const qualQuant = groupAgg(filtered, (i) => i.qual_quant || "Unspecified")
      .map((g) => ({ key: g.key, count: g.count }))
      .sort((a, b) => b.count - a.count);

    const withTotal = filtered.filter((i) => i.days_quote_to_brief != null);
    const timing = {
      q2c: stat(filtered.filter((i) => i.days_to_win != null).map((i) => i.days_to_win)),
      c2s: stat(filtered.filter((i) => i.days_com_to_sow != null).map((i) => i.days_com_to_sow)),
      s2b: stat(filtered.filter((i) => i.days_sow_to_brief != null).map((i) => i.days_sow_to_brief)),
      total: stat(withTotal.map((i) => i.days_quote_to_brief)),
      fastest: withTotal.length ? withTotal.reduce((a, b) => (a.days_quote_to_brief <= b.days_quote_to_brief ? a : b)) : null,
      slowest: withTotal.length ? withTotal.reduce((a, b) => (a.days_quote_to_brief >= b.days_quote_to_brief ? a : b)) : null,
    };

    const monthKeys = (data?.monthly || []).map((m) => m.month);
    const mg = Object.fromEntries(monthKeys.map((k) => [k, { count: 0, value: 0 }]));
    for (const i of filtered) {
      const k = (i.brief_date || "").slice(0, 7);
      if (mg[k]) { mg[k].count += 1; mg[k].value += i.value_gbp || 0; }
    }
    const monthly = monthKeys.map((k) => ({ month: k, ...mg[k] }));

    const values = filtered.filter((i) => i.value_gbp).map((i) => i.value_gbp);
    const bookValue = values.reduce((s, v) => s + v, 0);

    const withBoth = filtered.filter((i) => i.value_delta != null);
    const signedSum = withBoth.reduce((s, i) => s + (i.signed_value_gbp || 0), 0);
    const currentSum = withBoth.reduce((s, i) => s + (i.current_value_gbp || 0), 0);
    const variance = {
      n: withBoth.length,
      signed: signedSum,
      current: currentSum,
      delta: currentSum - signedSum,
      pct: signedSum ? ((currentSum - signedSum) / signedSum) * 100 : null,
      movers: withBoth
        .filter((i) => i.value_delta !== 0)
        .sort((a, b) => Math.abs(b.value_delta) - Math.abs(a.value_delta))
        .slice(0, 5),
    };

    const invoicing = {
      remaining: filtered.reduce((s, i) => s + (i.remaining_value || 0), 0),
      remainingCount: filtered.reduce((s, i) => s + (i.remaining_count || 0), 0),
      invoiced: filtered.reduce((s, i) => s + (i.invoiced_value || 0), 0),
      overdue: filtered.reduce((s, i) => s + (i.overdue_invoices || 0), 0),
    };

    const totals = {
      count: filtered.length,
      book_value: filtered.length ? bookValue : null,
      median_s2b: timing.s2b.median,
      median_total: timing.total.median,
    };

    return { filtered, byTeam, topClients, qualQuant, timing, monthly, totals, variance, invoicing };
  }, [data, teamFilter, clientFilter, clientIndex]);

  const items = useMemo(() => {
    const dir = sortDir === "desc" ? -1 : 1;
    return [...view.filtered].sort((a, b) => {
      const va = a[sortKey];
      const vb = b[sortKey];
      const cmp = typeof va === "string" || typeof vb === "string"
        ? String(va || "").localeCompare(String(vb || ""))
        : (va ?? -Infinity) - (vb ?? -Infinity);
      return dir * cmp;
    });
  }, [view.filtered, sortKey, sortDir]);

  const t = view.totals;
  const hasFilter = Boolean(teamFilter || clientFilter);

  return (
    <section
      id="briefed-book"
      data-testid="briefed-book"
      className="bg-white border border-stone-200 rounded-2xl overflow-hidden shadow-sm mt-6 scroll-mt-20"
    >
      <header className="px-5 pt-4 pb-3 border-b border-stone-100">
        <p className="text-[10px] font-mono tracking-[0.22em] uppercase text-indigo-700 mb-0.5">
          The briefed book
        </p>
        <p className="text-sm text-stone-600">
          Every project that has been <span className="font-medium text-slate-800">briefed to the delivery team</span> - the full journey from quote to commission to signature to brief. Click a team or client to drill down.
        </p>
      </header>

      {loading && (
        <div className="flex items-center gap-2 text-stone-500 text-sm px-5 py-8" data-testid="briefed-book-loading">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading the briefed book…
        </div>
      )}
      {!loading && error && (
        <div className="mx-5 my-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 flex items-start gap-2" data-testid="briefed-book-error">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {!loading && !error && data && (
        <>
          {hasFilter && (
            <div
              data-testid="briefed-filter-bar"
              className="flex flex-wrap items-center gap-2 px-5 py-2.5 border-b border-indigo-100 bg-indigo-50/40"
            >
              <span className="text-[10px] font-mono uppercase tracking-wider text-stone-500">
                Drilled into
              </span>
              {teamFilter && (
                <FilterChip
                  label={`Team: ${teamFilter}`}
                  onClear={() => setFilter("team", null)}
                  testid="briefed-filter-chip-team"
                  tone="indigo"
                />
              )}
              {clientFilter && (
                <FilterChip
                  label={`Client: ${clientIndex.get(clientFilter)?.label || clientFilter}`}
                  onClear={() => setFilter("client", null)}
                  testid="briefed-filter-chip-client"
                  tone="indigo"
                />
              )}
              <button
                type="button"
                onClick={clearAll}
                data-testid="briefed-filter-clear"
                className="text-xs font-semibold text-indigo-800 hover:text-indigo-600 transition-colors ml-1"
              >
                clear all
              </button>
            </div>
          )}

          {/* KPI strip */}
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-px bg-stone-100">
            {[
              { label: "Briefed projects", value: t.count?.toLocaleString() ?? "-", testid: "briefed-book-kpi-count" },
              {
                label: "Current value",
                value: fmtGbpShort(t.book_value),
                tone: "text-indigo-700",
                testid: "briefed-book-kpi-value",
                sub: view.variance.n > 0 && (
                  <span
                    className={
                      view.variance.delta > 0
                        ? "text-emerald-700"
                        : view.variance.delta < 0
                        ? "text-rose-700"
                        : "text-stone-400"
                    }
                    data-testid="briefed-book-kpi-variance"
                  >
                    {view.variance.delta === 0
                      ? "unchanged since signature"
                      : `${fmtDelta(view.variance.delta)} (${view.variance.pct > 0 ? "+" : ""}${view.variance.pct.toFixed(1)}%) since signed`}
                  </span>
                ),
              },
              { label: "Median SOW → briefed", value: t.median_s2b != null ? `${Math.round(t.median_s2b)}d` : "-", tone: "text-[#1B4F8A]", testid: "briefed-book-kpi-s2b" },
              { label: "Median end-to-end", value: t.median_total != null ? `${Math.round(t.median_total)}d` : "-", tone: "text-[#1B4F8A]", testid: "briefed-book-kpi-total" },
              {
                label: "Remaining to invoice",
                value: fmtGbpShort(view.invoicing.remaining),
                tone: "text-emerald-700",
                testid: "briefed-book-kpi-remaining",
                sub: view.invoicing.overdue > 0 ? (
                  <span className="text-rose-700" data-testid="briefed-book-kpi-overdue">
                    {view.invoicing.overdue} overdue invoice{view.invoicing.overdue === 1 ? "" : "s"}
                  </span>
                ) : (
                  <span className="text-stone-400">{view.invoicing.remainingCount} invoice{view.invoicing.remainingCount === 1 ? "" : "s"} scheduled</span>
                ),
              },
            ].map((k) => (
              <div key={k.label} className="bg-white px-4 py-3" data-testid={k.testid}>
                <p className="text-[10px] font-mono uppercase tracking-wider text-stone-500 mb-1">{k.label}</p>
                <p className={`text-2xl font-medium tabular-nums leading-none ${k.tone || "text-slate-900"}`}>{k.value}</p>
                {k.sub && <p className="text-[10px] font-mono mt-1">{k.sub}</p>}
              </div>
            ))}
          </div>

          {/* Charts grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 px-5 py-4 border-t border-stone-100 bg-stone-50/50">
            <TeamPie byTeam={view.byTeam} selected={teamFilter} onSelect={toggleTeam} idPrefix="briefed" />
            <div className="flex flex-col gap-4">
              <TimingChain
                title="Quote → Commission → SOW → Briefed"
                stages={["Quote", "Comm", "SOW", "Brief"]}
                legs={[
                  { days: view.timing.q2c.median, testid: "briefed-timing-q2c" },
                  { days: view.timing.c2s.median, testid: "briefed-timing-c2s" },
                  { days: view.timing.s2b.median, testid: "briefed-timing-s2b" },
                ]}
                total={view.timing.total}
                fastest={view.timing.fastest && { name: view.timing.fastest.client_company, days: view.timing.fastest.days_quote_to_brief }}
                slowest={view.timing.slowest && { name: view.timing.slowest.client_company, days: view.timing.slowest.days_quote_to_brief }}
                idPrefix="briefed"
                accent="#4F46E5"
              />
              <QualQuantMix mix={view.qualQuant} />
            </div>
            <TopClients
              clients={view.topClients}
              selected={clientFilter}
              onSelect={toggleClient}
              title="Top clients by value"
              idPrefix="briefed"
            />
            <div className="md:col-span-1 xl:col-span-1">
              <ValueVariance variance={view.variance} idPrefix="briefed" accentClass="text-indigo-700" />
            </div>
            <div className="md:col-span-2 xl:col-span-2">
              <MonthlyTrend
                monthly={view.monthly}
                title="Briefs per month · last 12 months"
                color="#4F46E5"
                idPrefix="briefed"
              />
            </div>
          </div>

          {/* Project table */}
          <div className="overflow-x-auto border-t border-stone-100">
            <table className="w-full text-sm" data-testid="briefed-book-table">
              <thead>
                <tr className="text-[10px] font-mono uppercase tracking-wider text-stone-400 border-b border-stone-100">
                  <th className="px-5 py-2.5 text-left font-medium">Client / project</th>
                  <th className="px-3 py-2.5 text-left font-medium">Team</th>
                  <th className="px-3 py-2.5 text-right font-medium">
                    <SortBtn label="Signed £" k="value_gbp" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} testid="briefed-book-sort-value" />
                  </th>
                  <th className="px-3 py-2.5 text-right font-medium">
                    <SortBtn label="Δ since signed" k="value_delta" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} testid="briefed-book-sort-delta" />
                  </th>
                  <th className="px-3 py-2.5 text-right font-medium">Quoted</th>
                  <th className="px-3 py-2.5 text-right font-medium">Commissioned</th>
                  <th className="px-3 py-2.5 text-right font-medium">SOW signed</th>
                  <th className="px-3 py-2.5 text-right font-medium">
                    <SortBtn label="Briefed" k="brief_date" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} testid="briefed-book-sort-brief-date" />
                  </th>
                  <th className="px-3 py-2.5 text-right font-medium">S→B</th>
                  <th className="px-3 py-2.5 text-right font-medium">
                    <SortBtn label="Total" k="days_quote_to_brief" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} testid="briefed-book-sort-total" />
                  </th>
                  <th className="px-3 py-2.5 text-right font-medium">Invoices</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, i) => (
                  <Fragment key={item.cid ?? i}>
                  <tr
                    data-testid={`briefed-book-row-${item.cid}`}
                    className="border-b border-stone-50 hover:bg-stone-50/60 transition-colors"
                  >
                    <td className="px-5 py-2.5">
                      <button
                        type="button"
                        onClick={() => toggleClient(clientKey(item))}
                        data-testid={`briefed-row-client-${item.cid}`}
                        title={`Drill into ${item.client_company || "Unknown"}`}
                        className="font-medium text-slate-900 leading-snug truncate max-w-[240px] block text-left hover:text-[#1B4F8A] hover:underline transition-colors"
                      >
                        {item.client_company || "Unknown client"}
                      </button>
                      <p className="text-[11px] text-stone-500 truncate max-w-[240px]">
                        {item.topic || item.project_name || ""}
                      </p>
                      <p className="text-[10px] font-mono text-stone-400 mt-0.5">
                        {item.cognito_admin ? (
                          <a
                            href={item.cognito_admin}
                            target="_blank"
                            rel="noreferrer"
                            title="Open in Cognito (admin)"
                            className="hover:text-[#1B4F8A] hover:underline transition-colors"
                          >
                            #{item.cid}
                          </a>
                        ) : (
                          <span>#{item.cid}</span>
                        )}
                        {item.project_number && <span> · {item.project_number}</span>}
                      </p>
                    </td>
                    <td className="px-3 py-2.5">
                      <button
                        type="button"
                        onClick={() => toggleTeam(item.team)}
                        data-testid={`briefed-row-team-${item.cid}`}
                        title={`Drill into team ${item.team}`}
                        className="inline-flex items-center gap-1.5 text-[11px] font-medium text-slate-700 hover:text-[#1B4F8A] transition-colors"
                      >
                        <span
                          className="w-2 h-2 rounded-full shrink-0"
                          style={{ background: teamColor(item.team, 0) }}
                        />
                        {item.team}
                      </button>
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <span className="font-mono tabular-nums text-slate-700">
                        {fmtGbp(item.signed_value_gbp ?? item.value_gbp)}
                      </span>
                      {item.value_delta != null && item.value_delta !== 0 && (
                        <p className="text-[9px] font-mono text-stone-400 mt-0.5">
                          now {fmtGbp(item.current_value_gbp)}
                        </p>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono tabular-nums text-xs" data-testid={`briefed-row-delta-${item.cid}`}>
                      {item.value_delta == null ? (
                        <span className="text-stone-300">-</span>
                      ) : item.value_delta === 0 ? (
                        <span className="text-stone-300">±0</span>
                      ) : (
                        <span className={item.value_delta > 0 ? "text-emerald-700" : "text-rose-700"}>
                          {fmtDelta(item.value_delta)}
                          {item.value_delta_pct != null && (
                            <span className="text-[9px] ml-0.5">
                              ({item.value_delta_pct > 0 ? "+" : ""}{item.value_delta_pct}%)
                            </span>
                          )}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono tabular-nums text-xs text-stone-500 whitespace-nowrap">
                      {fmtDate(item.proposal_date)}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono tabular-nums text-xs text-stone-500 whitespace-nowrap">
                      {fmtDate(item.com_date)}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono tabular-nums text-xs text-stone-500 whitespace-nowrap">
                      {fmtDate(item.sow_date)}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono tabular-nums text-xs text-slate-700 whitespace-nowrap">
                      {fmtDate(item.brief_date)}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono tabular-nums text-xs text-stone-500">
                      {item.days_sow_to_brief != null ? `${item.days_sow_to_brief}d` : "-"}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono tabular-nums font-semibold text-[#1B4F8A]">
                      {item.days_quote_to_brief != null ? `${item.days_quote_to_brief}d` : "-"}
                    </td>
                    <InvoiceToggleCell
                      item={item}
                      expanded={expandedCid === item.cid}
                      onToggle={() => setExpandedCid(expandedCid === item.cid ? null : item.cid)}
                      idPrefix="briefed"
                    />
                  </tr>
                  {expandedCid === item.cid && (
                    <tr data-testid={`briefed-invoice-schedule-row-${item.cid}`} className="bg-stone-50/60">
                      <td colSpan={11} className="px-5 py-3">
                        <InvoiceSchedule invoices={item.invoices} expected={item.expected_invoices} idPrefix="briefed" cid={item.cid} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
              </tbody>
            </table>
            {items.length === 0 && (
              <p className="px-5 py-8 text-sm text-stone-400" data-testid="briefed-book-empty">
                {hasFilter
                  ? "No briefed projects match the current drill-down."
                  : "No projects at BRIEFED in the Schedule of Work."}
              </p>
            )}
          </div>
        </>
      )}
    </section>
  );
}
